import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';

import useDebounce from 'shared_DEPRECATED/hooks/useDebounce';

import { localStorage } from 'shared/services/localStorage';

const DEBOUNCE_TIMEOUT = 200;

interface ResizeContextType {
  startItemSize: number;
  endItemSize: string;
  setStartItemSize: (size: number) => void;
  isResize: boolean;
  setIsResize: (isResize: boolean) => void;
  isHovered: boolean;
  setIsHovered: (isHovered: boolean) => void;
  minStartSize: number;
  maxStartSize: number;
  defaultStartSize: number;
}

const ResizeContext = createContext<ResizeContextType | null>(null);

interface ResizeProviderProps {
  children: React.ReactNode;
  storageKey: string;
  defaultStartSize?: number; // percentage value
  minStartSize?: number; // percentage value
  maxStartSize?: number; // percentage value
}

const convertPercentageToPixels = (
  percentage: number,
  containerSize: number
): number => {
  return Math.round((percentage / 100) * containerSize);
};

const convertPixelsToPercentage = (
  pixels: number,
  containerSize: number
): number => {
  return Math.round((pixels / containerSize) * 100);
};

export const ResizeProvider = ({
  children,
  storageKey,
  defaultStartSize = 25,
  minStartSize = 25,
  maxStartSize = 65,
}: ResizeProviderProps) => {
  if (!defaultStartSize) {
    throw new Error(
      'useResizeContext: Error: defaultStartSize prop is required'
    );
  }

  if (minStartSize >= maxStartSize) {
    throw new Error(
      'useResizeContext: Error: minStartSize must be less than maxStartSize'
    );
  }

  // Get the container size
  const getContainerSize = useCallback(() => {
    return window.innerWidth;
  }, []);

  // Get the current percentage size
  const getCurrentPercentage = useCallback(() => {
    return localStorage.getItem<number>(storageKey) ?? defaultStartSize;
  }, [storageKey, defaultStartSize]);

  // Convert stored percentage to pixels
  const getInitialPixelSize = useCallback(() => {
    const containerSize = getContainerSize();
    const storedPercentage = getCurrentPercentage();
    return convertPercentageToPixels(storedPercentage, containerSize);
  }, [getContainerSize, getCurrentPercentage]);

  const [startItemSize, setStartItemSize] = useState(getInitialPixelSize());
  const [isResize, setIsResize] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const debouncedStartItemSize = useDebounce(startItemSize, DEBOUNCE_TIMEOUT);

  const endItemSize = `calc(100% - ${startItemSize}px)`;

  useEffect(() => {
    const handleResize = () => {
      const containerSize = getContainerSize();
      const currentPercentage = getCurrentPercentage();
      const newPixelSize = convertPercentageToPixels(
        currentPercentage,
        containerSize
      );
      setStartItemSize(newPixelSize);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [getContainerSize, getCurrentPercentage]);

  useEffect(() => {
    if (!isResize) {
      const currentPercentage = convertPixelsToPercentage(
        debouncedStartItemSize,
        getContainerSize()
      );
      localStorage.setItem(storageKey, currentPercentage);
    }
  }, [isResize, debouncedStartItemSize, storageKey, getContainerSize]);

  const minStartSizePixels = convertPercentageToPixels(
    minStartSize,
    getContainerSize()
  );
  const maxStartSizePixels = convertPercentageToPixels(
    maxStartSize,
    getContainerSize()
  );
  const defaultStartSizePixels = convertPercentageToPixels(
    defaultStartSize,
    getContainerSize()
  );

  return (
    <ResizeContext.Provider
      value={{
        startItemSize,
        endItemSize,
        setStartItemSize,
        isResize,
        setIsResize,
        isHovered,
        setIsHovered,
        minStartSize: minStartSizePixels,
        maxStartSize: maxStartSizePixels,
        defaultStartSize: defaultStartSizePixels,
      }}
    >
      {children}
    </ResizeContext.Provider>
  );
};

export const useResizeContext = () => {
  const context = useContext(ResizeContext);

  if (!context) {
    throw new Error(
      'useResizeContext must be used within a ResizeContext.Provider'
    );
  }
  return context;
};
